import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AgentContext = createContext();

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};

export const AgentProvider = ({ children }) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();

  // Check if we're on an agent page
  const isAgentPage = location.pathname.startsWith('/agent/');
  
  // Extract agent slug from URL
  const getAgentSlug = () => {
    if (!isAgentPage) return null;
    const parts = location.pathname.split('/');
    return parts[2] || null;
  };

  const agentSlug = getAgentSlug();

  useEffect(() => {
    const fetchAgent = async () => {
      if (!agentSlug) {
        setAgent(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`${API_URL}/api/agents/${agentSlug}`);
        setAgent(response.data);
      } catch (err) {
        console.error('Error fetching agent:', err);
        setError(err.response?.data?.detail || 'Agent not found');
        setAgent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAgent();
  }, [agentSlug]);

  // Get contact info - returns agent info if on agent page, otherwise company info
  const getContactInfo = () => {
    if (agent) {
      return {
        phone: agent.phone,
        email: agent.email,
        name: agent.name,
        photo: agent.photo,
        isAgent: true
      };
    }
    return {
      phone: '1-877-239-9060',
      email: 'hello@utmostfinancial.ca',
      name: 'Utmost Financial Inc.',
      photo: null,
      isAgent: false
    };
  };

  // Build agent-aware links
  const buildLink = (path) => {
    if (agent && agentSlug) {
      // Remove leading slash if present
      const cleanPath = path.startsWith('/') ? path.slice(1) : path;
      return `/agent/${agentSlug}/${cleanPath}`;
    }
    return path;
  };

  const value = {
    agent,
    loading,
    error,
    isAgentPage,
    agentSlug,
    getContactInfo,
    buildLink
  };

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};

export default AgentContext;
